import { createApp } from "vue";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import { defaultOptions } from "@primevue/core/config";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";

import App from "./App.vue";
import router from "./router";
import { printhqPreset } from "./theme";
import { useAuthStore } from "./stores/auth";

// Styles
// Inter is imported from JS: main.css's own @import of it leaves the font's
// files unresolved through Tailwind, so the app fell back to the system font.
import "@fontsource-variable/inter";
import "./styles/main.css";
import "./styles/icons.css";

// ─── Tab recovery ──────────────────────────────────────────────────────
// Browsers can freeze/discard tabs. When restored from bfcache the JS
// state is stale (dead WebSockets, expired tokens). A full reload is the
// only reliable recovery.
window.addEventListener("pageshow", (e) => {
	if ((e as PageTransitionEvent).persisted) {
		window.location.reload();
	}
});

// After returning from a long background period, PrimeVue modal masks
// can get stuck in a state where they intercept all clicks (the dialog
// is still "visible" but the Vue component tree lost its event bindings).
// This watchdog detects that case and cleans up orphaned masks.
(() => {
	let hiddenAt = 0;

	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") {
			hiddenAt = Date.now();
			return;
		}

		const elapsed = hiddenAt ? Date.now() - hiddenAt : 0;
		hiddenAt = 0;
		if (elapsed < 3_000) return; // short switch, skip

		// Give the app a moment to settle after wake, then check for
		// stuck overlays that intercept pointer events.
		setTimeout(() => {
			const masks = document.querySelectorAll<HTMLElement>(".p-dialog-mask");
			masks.forEach((mask) => {
				// A healthy modal mask has a visible dialog child. If the dialog
				// inside is gone or hidden, the mask is orphaned → remove it.
				const dialog = mask.querySelector(".p-dialog");
				if (!dialog || (dialog as HTMLElement).offsetParent === null) {
					console.warn("[recovery] Removing orphaned dialog mask", mask);
					mask.remove();
				}
			});

			// Also look for any element covering the center of the viewport
			// that isn't the app root or a visible dialog.
			const cx = window.innerWidth / 2;
			const cy = window.innerHeight / 2;
			const top = document.elementFromPoint(cx, cy);
			if (top && top !== document.documentElement && top !== document.body) {
				const isOverlay =
					top.classList.contains("p-overlay-mask") ||
					top.classList.contains("p-overlay-mask-leave") ||
					(top.classList.contains("p-dialog-mask") && !top.querySelector(".p-dialog"));
				if (isOverlay) {
					console.warn("[recovery] Removing blocking overlay at viewport center", top);
					top.remove();
				}
			}
		}, 800);
	});
})();

async function bootstrap() {
	const app = createApp(App);
	const pinia = createPinia();

	// Plugins
	app.use(pinia);
	app.use(router);
	app.use(PrimeVue, {
		theme: {
			preset: printhqPreset,
			options: {
				darkModeSelector: ".dark",
				cssLayer: false,
			},
		},
		ripple: false,
		// Confirmations without their own labels still read as the design
		// system asks: "Cancel", never "No".
		locale: { ...defaultOptions.locale, accept: "Confirm", reject: "Cancel" },
	});
	app.use(ToastService);
	app.use(ConfirmationService);

	// Directives
	app.directive("tooltip", Tooltip);

	// Global error handler — surfaces Vue component errors that would
	// otherwise silently break the UI (especially after tab backgrounding).
	app.config.errorHandler = (err, instance, info) => {
		console.error(`[Vue error] ${info}:`, err);
	};

	// Initialize auth before mounting
	const authStore = useAuthStore();
	await authStore.initialize();

	// Wait for the router to resolve the initial route (including guards/redirects)
	// This prevents the dashboard from flashing before redirecting to login
	await router.isReady();

	// Mount app
	app.mount("#app");
}

bootstrap().catch((err) => {
	console.error("App failed to initialize:", err);
	const el = document.getElementById("app");
	if (el) {
		el.innerHTML = `<div style="padding:2rem;font-family:system-ui;color:#dc2626"><h2>Failed to load PrintHQ</h2><pre>${err?.message || err}</pre></div>`;
	}
});
